import { useCallback, useMemo } from 'react';
import { Star, StarOutline } from '@mui/icons-material';
import { favourizeCompany } from '../../api/users';
import { useToggle, useURLParams, useUser } from '../../hooks';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { Row } from '../../styles';
import { Institution } from '../../types/Institution';
import { isCompany, isObjectEmpty } from '../../utils';
import { BoldedText } from '../bolded-text/BoldedText';
import { IconButton } from '../buttons';
import { IconMenu } from '../icon-menu/IconMenu';
import { ProfilePicture } from '../profile-picture/ProfilePicture';
import { InstitutionTableRow, InstitutionTitleMedium } from './styles';

const BOLD_STYLE = { fontWeight: 700 };

type Props = {
    institution: Institution;
};

const InstitutionRow = ({ institution }: Props) => {
    const { name, documentId } = institution;
    const { searchTerm } = useURLParams();
    const { user } = useUser();
    const [isFavourite, toggleIsFavourite] = useToggle(true);
    const { navigateToCompanyDetails } = useCustomNavigate();

    const handleRowClick = useCallback(() => {
        navigateToCompanyDetails(documentId);
    }, [navigateToCompanyDetails, documentId]);

    const handleStarClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (user && isCompany(institution)) {
                toggleIsFavourite();
                favourizeCompany(user.id, institution, !isFavourite);
            }
        },
        [user, institution, isFavourite, toggleIsFavourite]
    );

    const title = useMemo(() => {
        if (searchTerm && searchTerm.splitTerm) {
            return (
                <BoldedText
                    fullString={name}
                    inBold={searchTerm.splitTerm}
                    style={BOLD_STYLE}
                />
            );
        } else {
            return name;
        }
    }, [name, searchTerm]);

    return (
        <InstitutionTableRow aria-label='institution row' onClick={handleRowClick}>
            <Row gap={2}>
                <ProfilePicture imageUrl={institution.logo?.url} type='company' />
                <InstitutionTitleMedium
                    mr={0.5}
                    style={{ fontWeight: !isObjectEmpty(searchTerm) ? 500 : 600 }}
                >
                    {title}
                </InstitutionTitleMedium>
            </Row>

            <Row gap={1} style={{ justifyContent: 'flex-end' }}>
                <IconButton
                    icon={isFavourite ? <Star color={'primary'} /> : <StarOutline />}
                    onClick={handleStarClick}
                />
                <IconMenu items={[]} />
            </Row>
        </InstitutionTableRow>
    );
};
export { InstitutionRow };
